import type { FloorPath } from "building-types/floors/floorTypes";
import type { FoundationPath } from "building-types/foundations/foundationTypes";
import type { Table } from "common/data/table";
import { planarGraphHalfEdgeEnd, planarGraphHalfEdgeStart } from "geom/planarGraph";
import type { PlanarGraph } from "geom/planarGraph";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";

export function pathStartCoordinates(planarGraph: PlanarGraph, halfEdgeIndex: number): [number, number] {
  const [x, y] = planarGraphHalfEdgeStart(planarGraph, halfEdgeIndex);
  return [x, y];
}

export function pathEndCoordinates(planarGraph: PlanarGraph, halfEdgeIndex: number): [number, number] {
  const [x, y] = planarGraphHalfEdgeEnd(planarGraph, halfEdgeIndex);
  return [x, y];
}

export function pathCoordinates(planarGraph: PlanarGraph, halfEdgeIndex: number): [number, number, number, number] {
  const [startX, startY] = pathStartCoordinates(planarGraph, halfEdgeIndex);
  const [endX, endY] = pathEndCoordinates(planarGraph, halfEdgeIndex);
  return [startX, startY, endX, endY];
}

export function floorPathCoordinates(
  floorPath: FloorPath,
  planarGraphs: Table<PolygonPlanarGraph>,
): [number, number, number, number] {
  const planarGraph = planarGraphs[floorPath.planarGraphId];
  if (!planarGraph) {
    throw new Error(
      `Planar graph not found for planarGraphId: ${floorPath.planarGraphId} (halfEdgeIndex: ${floorPath.halfEdgeIndex})`,
    );
  }
  return pathCoordinates(planarGraph.planarGraph, floorPath.halfEdgeIndex);
}

export function foundationPathCoordinates(
  foundationPath: Pick<FoundationPath, "halfEdgeIndex" | "planarGraphId">,
  planarGraphs: Table<PolygonPlanarGraph>,
): [number, number, number, number] {
  const planarGraph = planarGraphs[foundationPath.planarGraphId];
  if (!planarGraph) {
    throw new Error(
      `Planar graph not found for planarGraphId: ${foundationPath.planarGraphId} (halfEdgeIndex: ${foundationPath.halfEdgeIndex})`,
    );
  }
  return pathCoordinates(planarGraph.planarGraph, foundationPath.halfEdgeIndex);
}

const LINE_KEY_PRECISION = 6;

/**
 * Builds a key for the infinite line containing a segment so that strips on the same
 * (collinear) line get the same key. Key is (angle in [0, PI), signed distance from origin).
 * Used to group foundation strips for 2D profile union before creating analytic extrusions.
 */
export function lineKeyFromSegment(startX: number, startY: number, endX: number, endY: number): string {
  const dx = endX - startX;
  const dy = endY - startY;
  let angle = Math.atan2(dy, dx);
  if (angle < 0) {
    angle += Math.PI;
  }
  if (angle === Math.PI) {
    angle = 0;
  }
  const signedDist = startX * Math.sin(angle) - startY * Math.cos(angle);
  return `${angle.toFixed(LINE_KEY_PRECISION)}|${signedDist.toFixed(LINE_KEY_PRECISION)}`;
}

export function validatePathAlignment(
  foundationPath: Pick<FoundationPath, "halfEdgeIndex" | "planarGraphId">,
  floorPath: Pick<FloorPath, "halfEdgeIndex" | "planarGraphId">,
): void {
  if (foundationPath.planarGraphId !== floorPath.planarGraphId) {
    throw new Error(
      `Path alignment error: FoundationPath and FloorPath reference different planar graphs. ` +
        `FoundationPath.planarGraphId: ${foundationPath.planarGraphId}, ` +
        `FloorPath.planarGraphId: ${floorPath.planarGraphId}`,
    );
  }

  if (foundationPath.halfEdgeIndex !== floorPath.halfEdgeIndex) {
    throw new Error(
      `Path alignment error: FoundationPath and FloorPath reference different half edges. ` +
        `FoundationPath.halfEdgeIndex: ${foundationPath.halfEdgeIndex}, ` +
        `FloorPath.halfEdgeIndex: ${floorPath.halfEdgeIndex}`,
    );
  }
}

export function getAlignedPathCoordinates(
  foundationPath: Pick<FoundationPath, "halfEdgeIndex" | "planarGraphId">,
  floorPath: Pick<FloorPath, "halfEdgeIndex" | "planarGraphId">,
  planarGraphs: Table<PolygonPlanarGraph>,
): [number, number, number, number] {
  validatePathAlignment(foundationPath as FoundationPath, floorPath as FloorPath);
  return floorPathCoordinates(floorPath as FloorPath, planarGraphs);
}
